import {
	AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, NgZone,
	OnChanges, OnDestroy, Output, SimpleChanges, ViewChild
} from '@angular/core';
import {
	RseObjectFit,
	RsePlayerState,
	RseWhepSession,
} from '@ramsys/stream-player-core';
import { RseZoomController } from './rse-player-zoom';

/**
 * Angular 13 wrapper around @ramsys/stream-player-core (same inputs as eye's <rse-player>).
 *
 * Owns buffering overlay + WHEP/session retry so hosts (video-feeds, media, …) do not
 * re-implement connecting / playing / error handling.
 * Optional UI zoom/pan via [enableZoom] (shared RseZoomController).
 */
@Component({
	selector: 'rse-player',
	template: `
		<div
			class="rse-zoom-viewport"
			#zoomViewport
			[class.is-zoomed]="enableZoom && zoomCtrl.isZoomed"
			[class.is-panning]="enableZoom && zoomCtrl.isPanning"
			(wheel)="onZoomWheel($event)"
		>
			<div
				class="rse-zoom-layer"
				[class.no-transition]="enableZoom && zoomCtrl.isPanning"
				[style.transform]="enableZoom ? zoomCtrl.transform : null"
			>
				<video
					#video
					autoplay
					playsinline
					[muted]="muted"
					[attr.controls]="controls ? '' : null"
				></video>
			</div>
			<div
				*ngIf="enableZoom && zoomCtrl.isZoomed"
				class="rse-pan-surface"
				(pointerdown)="onPanStart($event)"
			></div>
			<div class="rse-loading" *ngIf="showLoadingOverlay && showsLoadingOverlay()">
				{{ 'VIDEO_FEED.STREAM_LOADING' | translate }}
			</div>
			<div class="rse-zoom-toolbar" *ngIf="enableZoom && showZoomControls" (pointerdown)="$event.stopPropagation()">
				<button type="button" class="rse-zoom-btn" (click)="zoomCtrl.zoomOut($event)" [disabled]="!zoomCtrl.canZoomOut"
					[title]="'VIDEO_CONTROL.ZOOM_OUT' | translate">
					<i class="fa fa-search-minus" aria-hidden="true"></i>
				</button>
				<button type="button" class="rse-zoom-btn rse-zoom-label" (click)="zoomCtrl.reset($event)"
					[title]="'VIDEO_CONTROL.ZOOM_RESET' | translate">
					{{ zoomCtrl.percentLabel }}
				</button>
				<button type="button" class="rse-zoom-btn" (click)="zoomCtrl.zoomIn($event)" [disabled]="!zoomCtrl.canZoomIn"
					[title]="'VIDEO_CONTROL.ZOOM_IN' | translate">
					<i class="fa fa-search-plus" aria-hidden="true"></i>
				</button>
			</div>
		</div>
	`,
	styles: [`
		:host {
			display: block;
			position: relative;
			width: 100%;
			height: 100%;
			background: #000;
			overflow: hidden;
		}
		.rse-zoom-viewport {
			position: relative;
			width: 100%;
			height: 100%;
			overflow: hidden;
		}
		.rse-zoom-viewport.is-zoomed {
			cursor: grab;
		}
		.rse-zoom-viewport.is-panning {
			cursor: grabbing;
		}
		.rse-zoom-layer {
			width: 100%;
			height: 100%;
			transform-origin: center center;
			transition: transform 0.12s ease-out;
			will-change: transform;
		}
		.rse-zoom-layer.no-transition {
			transition: none;
		}
		.rse-pan-surface {
			position: absolute;
			inset: 0;
			z-index: 5;
			touch-action: none;
			cursor: inherit;
			background: transparent;
		}
		video {
			display: block;
			width: 100%;
			height: 100%;
			background: #000;
			object-fit: var(--rse-object-fit, fill);
		}
		.rse-loading {
			position: absolute;
			inset: 0;
			z-index: 6;
			display: flex;
			align-items: center;
			justify-content: center;
			margin: 0;
			padding: 0.75rem;
			background: rgba(0, 0, 0, 0.55);
			color: #fff;
			font-size: 0.875rem;
			pointer-events: none;
		}
		.rse-zoom-toolbar {
			position: absolute;
			left: 50%;
			bottom: 8px;
			z-index: 7;
			display: inline-flex;
			align-items: center;
			gap: 4px;
			padding: 3px;
			border-radius: 8px;
			background: rgba(0, 0, 0, 0.45);
			transform: translateX(-50%);
		}
		.rse-zoom-btn {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			min-width: 28px;
			height: 28px;
			padding: 0 6px;
			border: 0;
			border-radius: 6px;
			background: rgba(0, 0, 0, 0.55);
			color: #fff;
			cursor: pointer;
		}
		.rse-zoom-btn:disabled {
			opacity: 0.35;
			cursor: default;
		}
		.rse-zoom-label {
			font-size: 11px;
			font-variant-numeric: tabular-nums;
		}
	`]
})
export class RsePlayerComponent implements AfterViewInit, OnChanges, OnDestroy {
	private static readonly MAX_WHEP_RETRIES = 20;
	private static readonly WHEP_RETRY_MS = 1500;
	private static readonly MAX_RELOADS = 8;
	private static readonly RELOAD_DELAY_MS = 2500;
	private static readonly RELOAD_404_DELAY_MS = 4500;

	@Input() serverUrl = '';
	@Input() url = '';
	@Input() appToken = '';
	@Input() sessionToken = '';
	@Input() muted = true;
	@Input() controls = false;
	@Input() objectFit: RseObjectFit = 'fill';
	@Input() iceServers: RTCIceServer[] = [];
	@Input() iceGatherTimeoutMs = 2000;
	/** Built-in buffering overlay (same rules as eye live-feed). */
	@Input() showLoadingOverlay = true;
	/** Enable CSS zoom/pan on the video surface. */
	@Input() enableZoom = false;
	/** Show built-in − / % / + toolbar when enableZoom is on. */
	@Input() showZoomControls = false;

	@Output() stateChange = new EventEmitter<RsePlayerState>();
	@Output() playbackError = new EventEmitter<Error>();

	@ViewChild('video', { static: true }) videoRef: ElementRef<HTMLVideoElement>;
	@ViewChild('zoomViewport') zoomViewport?: ElementRef<HTMLElement>;

	@HostBinding('style.--rse-object-fit')
	get objectFitStyle(): string {
		return this.objectFit;
	}

	/** Last emitted session state (for parents that need a sync read). */
	state: RsePlayerState = 'idle';
	/** True while we consider the player buffering (connecting / reconnecting). */
	loading = false;
	readonly zoomCtrl = new RseZoomController();

	private session: RseWhepSession | null = null;
	private viewReady = false;
	private lastKey = '';
	private whepRetryAttempt = 0;
	private whepRetryTimer: ReturnType<typeof setTimeout> | null = null;
	private reloadAttempt = 0;
	private reloadTimer: ReturnType<typeof setTimeout> | null = null;
	/** When true, ignore the session's synchronous `error` state after a swallowed retryable failure. */
	private pendingWhepRetry = false;
	private destroyed = false;
	private readonly onPageHide = () => {
		void this.session?.stop();
	};

	constructor(private readonly zone: NgZone) { }

	ngAfterViewInit(): void {
		this.viewReady = true;
		this.syncZoomViewport();
		window.addEventListener('pagehide', this.onPageHide);
		this.open();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['enableZoom'] || changes['url']) {
			this.zoomCtrl.reset();
			this.syncZoomViewport();
		}
		if (!this.viewReady) {
			return;
		}
		if (
			changes['serverUrl'] ||
			changes['url'] ||
			changes['appToken'] ||
			changes['sessionToken'] ||
			changes['iceServers'] ||
			changes['iceGatherTimeoutMs']
		) {
			this.whepRetryAttempt = 0;
			this.reloadAttempt = 0;
			this.clearWhepRetry();
			this.clearReloadTimer();
			this.open();
		}
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.clearWhepRetry();
		this.clearReloadTimer();
		this.zoomCtrl.endPan();
		window.removeEventListener('pagehide', this.onPageHide);
		void this.session?.stop();
		this.session = null;
	}

	@HostListener('document:pointermove', ['$event'])
	onDocumentPointerMove(event: PointerEvent): void {
		if (!this.enableZoom) {
			return;
		}
		this.zoomCtrl.movePan(event);
	}

	@HostListener('document:pointerup', ['$event'])
	@HostListener('document:pointercancel', ['$event'])
	onDocumentPointerUp(event: PointerEvent): void {
		if (!this.enableZoom) {
			return;
		}
		this.zoomCtrl.endPan(event);
	}

	reload(): void {
		this.whepRetryAttempt = 0;
		this.clearWhepRetry();
		this.lastKey = '';
		this.loading = true;
		this.emitState('connecting');
		this.open();
	}

	/** True when the video element already has a painted frame (reuse after remount/tiling). */
	hasRenderableFrame(): boolean {
		const video = this.videoRef?.nativeElement;
		return !!video && video.videoWidth > 0 && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
	}

	/**
	 * Overlay only while buffering with no painted frame.
	 * Ignores playing / error / closed so reused players don't show a false loader.
	 */
	showsLoadingOverlay(): boolean {
		if (this.state === 'playing' || this.state === 'error' || this.state === 'closed') {
			return false;
		}
		if (!(this.loading || this.state === 'connecting')) {
			return false;
		}
		return !this.hasRenderableFrame();
	}

	onZoomWheel(event: WheelEvent): void {
		if (!this.enableZoom) {
			return;
		}
		this.zoomCtrl.onWheel(event);
	}

	onPanStart(event: PointerEvent): void {
		if (!this.enableZoom) {
			return;
		}
		this.syncZoomViewport();
		this.zoomCtrl.startPan(event);
	}

	private syncZoomViewport(): void {
		this.zoomCtrl.setViewport(this.zoomViewport?.nativeElement || null);
	}

	private open(): void {
		const video = this.videoRef?.nativeElement;
		if (!video) {
			return;
		}
		const key = JSON.stringify({
			serverUrl: this.serverUrl,
			url: this.url,
			appToken: this.appToken,
			sessionToken: this.sessionToken,
			iceServers: this.iceServers,
			iceGatherTimeoutMs: this.iceGatherTimeoutMs,
			whepRetryAttempt: this.whepRetryAttempt,
		});
		if (key === this.lastKey) {
			return;
		}
		this.lastKey = key;
		if (!this.session) {
			this.session = new RseWhepSession(
				(state) => this.zone.run(() => this.onSessionState(state)),
				(error) => this.zone.run(() => this.onPlaybackError(error)),
			);
		}
		void this.session.start(video, {
			serverUrl: this.serverUrl,
			url: this.url,
			appToken: this.appToken,
			sessionToken: this.sessionToken,
			iceServers: this.iceServers,
			iceGatherTimeoutMs: this.iceGatherTimeoutMs,
		});
	}

	private onSessionState(state: RsePlayerState): void {
		if (this.pendingWhepRetry && state === 'error') {
			return;
		}
		switch (state) {
			case 'connecting':
				this.loading = true;
				break;
			case 'playing':
				this.whepRetryAttempt = 0;
				this.reloadAttempt = 0;
				this.pendingWhepRetry = false;
				this.clearWhepRetry();
				this.clearReloadTimer();
				this.loading = false;
				break;
			case 'error':
				// Reload may be scheduled from onPlaybackError; keep overlay off if a frame exists.
				this.loading = false;
				break;
			default:
				break;
		}
		this.emitState(state);
	}

	private emitState(state: RsePlayerState): void {
		this.state = state;
		this.stateChange.emit(state);
	}

	private onPlaybackError(error: Error): void {
		if (this.destroyed) {
			return;
		}
		if (this.isRetryableWhepError(error)
			&& this.whepRetryAttempt < RsePlayerComponent.MAX_WHEP_RETRIES) {
			this.whepRetryAttempt += 1;
			this.clearWhepRetry();
			this.pendingWhepRetry = true;
			this.loading = true;
			this.emitState('connecting');
			this.whepRetryTimer = setTimeout(() => {
				this.whepRetryTimer = null;
				this.pendingWhepRetry = false;
				if (this.destroyed) {
					return;
				}
				this.lastKey = '';
				this.open();
			}, RsePlayerComponent.WHEP_RETRY_MS);
			return;
		}

		this.pendingWhepRetry = false;
		this.whepRetryAttempt = 0;
		this.loading = false;
		this.emitState('error');

		if (this.scheduleHardReload(error)) {
			return;
		}

		this.playbackError.emit(error);
	}

	/** Extra reconnect attempts after WHEP retries are exhausted (eye live-feed pattern). */
	private scheduleHardReload(error: Error): boolean {
		if (this.reloadTimer != null) {
			return true;
		}
		if (this.reloadAttempt >= RsePlayerComponent.MAX_RELOADS) {
			this.reloadAttempt = 0;
			return false;
		}

		const message = (error && error.message) ? String(error.message) : '';
		const delayMs = /WHEP request failed \(404\)/i.test(message)
			? RsePlayerComponent.RELOAD_404_DELAY_MS
			: RsePlayerComponent.RELOAD_DELAY_MS;

		this.reloadAttempt += 1;
		this.reloadTimer = setTimeout(() => {
			this.reloadTimer = null;
			if (this.destroyed) {
				return;
			}
			this.reload();
		}, delayMs);
		return true;
	}

	private isRetryableWhepError(error: Error): boolean {
		const message = (error && error.message) ? String(error.message) : '';
		return /WHEP request failed \((404|409|503)\)/i.test(message)
			|| /no stream is available/i.test(message)
			|| /stream not found/i.test(message);
	}

	private clearWhepRetry(): void {
		if (this.whepRetryTimer != null) {
			clearTimeout(this.whepRetryTimer);
			this.whepRetryTimer = null;
		}
	}

	private clearReloadTimer(): void {
		if (this.reloadTimer != null) {
			clearTimeout(this.reloadTimer);
			this.reloadTimer = null;
		}
	}
}
